import { Guideline, GUIDELINE_DEFAULT_COLOR, GUIDELINE_PAGE_COLOR } from './Guideline';

export default class GuidelineManager {
  _guidelines = new Map();
  _nextId = 1;
  gridSize = 20;
  _onChange = null;
  _attachments = new Map(); // guidelineId -> Set<HaspObject>
  _onAttachedObjectsMoved = null;
  stickyGuidelines = true;
  activePageId = null; // null or 0 = global page, N = page N

  constructor({ gridSize = 20 } = {}) {
    this.gridSize = gridSize;
  }

  setOnChange(cb) { this._onChange = cb; }

  setOnAttachedObjectsMoved(cb) { this._onAttachedObjectsMoved = cb; }

  setStickyGuidelines(value) {
    this.stickyGuidelines = value;
    if (!value) {
      for (const [guidelineId, attached] of this._attachments) {
        const gl = this._guidelines.get(guidelineId);
        if (!gl) continue;
        const axis = gl.orientation === 'vertical' ? 'vertical' : 'horizontal';
        for (const obj of attached) {
          if (obj.attachedGuidelines) obj.attachedGuidelines[axis] = null;
        }
      }
      this._attachments.clear();
    }
  }

  /**
   * Notify the manager that the active page changed.
   * Guidelines for the old page are hidden (remove events fired),
   * guidelines for the new page are shown (add events fired).
   * Global guidelines (pageId === null) are always visible and unaffected.
   */
  setActivePageId(newPageId) {
    const oldPageId = this.activePageId;

    // Fire remove events for guidelines that were specific to the old page
    if (oldPageId !== null && oldPageId !== 0) {
      for (const gl of this._guidelines.values()) {
        if (gl.pageId === oldPageId) {
          this._onChange?.('remove', gl);
        }
      }
    }

    this.activePageId = newPageId ?? null;

    // Fire add events for guidelines specific to the new page
    if (newPageId !== null && newPageId !== 0) {
      for (const gl of this._guidelines.values()) {
        if (gl.pageId === newPageId) {
          this._onChange?.('add', gl);
        }
      }
    }
  }

  addGuideline(orientation, rawPosition) {
    const isPageSpecific = this.activePageId !== null && this.activePageId !== 0;
    const pageId  = isPageSpecific ? this.activePageId : null;
    const color   = isPageSpecific ? GUIDELINE_PAGE_COLOR : GUIDELINE_DEFAULT_COLOR;
    const position = this.snapToGrid(rawPosition);
    const gl = new Guideline({ id: this._nextId++, orientation, position, color, pageId });
    this._guidelines.set(gl.id, gl);
    this._onChange?.('add', gl);
    return gl;
  }

  updatePosition(id, rawPosition) {
    const position = this.snapToGrid(rawPosition);
    const gl = this._guidelines.get(id);
    if (!gl) return rawPosition;

    const delta = position - gl.position;
    if (delta !== 0) {
      const attached = this._attachments.get(id);
      if (attached && attached.size > 0) {
        for (const obj of attached) {
          if (gl.orientation === 'vertical') {
            obj.x(obj.x() + delta);
          } else {
            obj.y(obj.y() + delta);
          }
        }
        this._onAttachedObjectsMoved?.();
      }
    }

    gl.position = position;
    this._onChange?.('update', gl);
    return position;
  }

  removeGuideline(id) {
    const gl = this._guidelines.get(id);
    if (!gl) return;

    // Detach all attached objects before removing
    const attached = this._attachments.get(id);
    if (attached) {
      const axis = gl.orientation === 'vertical' ? 'vertical' : 'horizontal';
      for (const obj of attached) {
        if (obj.attachedGuidelines) {
          obj.attachedGuidelines[axis] = null;
        }
      }
      this._attachments.delete(id);
    }

    this._guidelines.delete(id);
    this._onChange?.('remove', gl);
  }

  /**
   * Attach obj to the guideline with guidelineId.
   * Axis is derived from the guideline's orientation.
   * Replaces any existing attachment on that axis.
   */
  attachObject(guidelineId, obj) {
    if (!this.stickyGuidelines) return;
    const gl = this._guidelines.get(guidelineId);
    if (!gl) return;
    const axis = gl.orientation === 'vertical' ? 'vertical' : 'horizontal';

    if (!obj.attachedGuidelines) obj.attachedGuidelines = { vertical: null, horizontal: null };
    const prevId = obj.attachedGuidelines[axis];
    if (prevId === guidelineId) return; // already attached, nothing to do

    if (prevId !== null && prevId !== undefined) {
      this._removeFromAttachments(prevId, obj);
    }

    obj.attachedGuidelines[axis] = guidelineId;
    if (!this._attachments.has(guidelineId)) {
      this._attachments.set(guidelineId, new Set());
    }
    this._attachments.get(guidelineId).add(obj);
  }

  /**
   * Detach obj from whichever guideline it is attached to on the given axis.
   * axis = 'vertical' | 'horizontal'
   */
  detachObject(obj, axis) {
    if (!obj.attachedGuidelines) return;
    const guidelineId = obj.attachedGuidelines[axis];
    if (guidelineId === null || guidelineId === undefined) return;
    this._removeFromAttachments(guidelineId, obj);
    obj.attachedGuidelines[axis] = null;
  }

  _removeFromAttachments(guidelineId, obj) {
    const set = this._attachments.get(guidelineId);
    if (set) {
      set.delete(obj);
      if (set.size === 0) this._attachments.delete(guidelineId);
    }
  }

  // ── Persistence ────────────────────────────────────────────────────────────

  /** Serialize all guidelines (all pages) for inclusion in the design file. */
  exportGuidelines() {
    return [...this._guidelines.values()].map(gl => ({
      id:          gl.id,
      orientation: gl.orientation,
      position:    gl.position,
      color:       gl.color,
      pageId:      gl.pageId,
    }));
  }

  /**
   * Remove all guidelines from memory and fire 'remove' events for any that
   * currently have Konva nodes in GuidelineLayer (the visible ones).
   */
  clearGuidelines() {
    for (const gl of this.getGuidelines()) {
      this._onChange?.('remove', gl);
    }
    this._guidelines.clear();
    this._attachments.clear();
    this._nextId = 1;
  }

  /**
   * Restore guidelines from serialized data (produced by exportGuidelines).
   * Fires 'add' events only for guidelines that are visible at the current
   * activePageId (global + current page), so GuidelineLayer creates their nodes.
   */
  importGuidelines(data) {
    for (const entry of data) {
      const gl = new Guideline({
        id:          entry.id,
        orientation: entry.orientation,
        position:    entry.position,
        color:       entry.color  ?? GUIDELINE_DEFAULT_COLOR,
        pageId:      entry.pageId ?? null,
      });
      this._guidelines.set(gl.id, gl);
      this._nextId = Math.max(this._nextId, gl.id + 1);

      if (gl.pageId === null || gl.pageId === this.activePageId) {
        this._onChange?.('add', gl);
      }
    }
  }

  /**
   * Returns only guidelines visible for the active page:
   * global guidelines (pageId === null) + current page guidelines (pageId === activePageId).
   */
  getGuidelines() {
    return [...this._guidelines.values()].filter(gl =>
      gl.pageId === null || gl.pageId === this.activePageId
    );
  }

  snapToGrid(position) {
    if (!this.gridSize) return position;
    return Math.round(position / this.gridSize) * this.gridSize;
  }

  /**
   * Returns snap targets for the active page only (global + current page guidelines).
   * Each guideline target includes guidelineId so attachment can be tracked.
   */
  getSnapTargets(stageBounds) {
    const vertical = [];
    const horizontal = [];
    const b = stageBounds ?? { x: -10000, y: -10000, width: 20000, height: 20000 };
    for (const gl of this.getGuidelines()) {
      if (!gl.visible) continue;
      if (gl.orientation === 'vertical') {
        vertical.push({
          pos: gl.position,
          bounds: { x: gl.position, y: b.y, width: 0, height: b.height },
          type: 'guideline',
          guidelineId: gl.id,
        });
      } else {
        horizontal.push({
          pos: gl.position,
          bounds: { x: b.x, y: gl.position, width: b.width, height: 0 },
          type: 'guideline',
          guidelineId: gl.id,
        });
      }
    }
    return { vertical, horizontal };
  }
}
